import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Scale } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import soilService from '../../services/soil';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { fmtNum, maintenance } from '../../utils/soilAnalytes';
import type { AreaMaintenance, MaintenanceYield } from '../../types/soil';

const seasonText = (y: number) => `${y}–${String(y + 1).slice(2)}`;

function yieldLabel(y: MaintenanceYield): string {
  return y.source === 'estimate'
    ? `${seasonText(y.season_year)} crop estimate`
    : `${seasonText(y.season_year)} harvest${y.weighed ? '' : ' (estimated weights)'}`;
}

/**
 * What the crop takes off this planting, as the amount to put back to stand
 * still: the crop type's removal per tonne times a yield. The yield is this
 * season's crop estimate or a past harvest, or one typed in. It is not a
 * fertiliser recommendation, and says so: it ignores what the soil holds,
 * what the tree stores and what is lost on the way.
 */
export default function MaintenancePanel({ areaId }: { areaId: number }) {
  const navigate = useNavigate();
  const [data, setData] = useState<AreaMaintenance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<number | 'typed'>(0);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    let live = true;
    soilService.getMaintenance(areaId)
      .then((res) => { if (live && res.success && res.data) { setData(res.data); if (!res.data.yields.length) setChoice('typed'); } })
      .catch((e) => { if (live) setError(errorMessage(e, 'Could not load the maintenance figures')); });
    return () => { live = false; };
  }, [areaId]);

  const tPerHa = choice === 'typed' ? Number(typed) : data?.yields[choice]?.t_per_ha ?? NaN;
  const lines = useMemo(
    () => (data && Number.isFinite(tPerHa) && (choice !== 'typed' || typed !== '')
      ? maintenance(data.rates, tPerHa, data.area.area_hectares)
      : []),
    [data, tPerHa, typed, choice],
  );
  const hasRates = !!data && Object.keys(data.rates).length > 0;
  const editRates = () => navigate(`/orchard/soil/removal${data?.crop_type ? `?crop_type=${data.rates_from?.crop_type_id ?? data.crop_type.id}` : ''}`);

  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-2 min-w-0">
          <Scale className="h-4 w-4 text-neutral-500 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-neutral-700">Crop removal and maintenance</h3>
            <p className="text-xs text-neutral-500">What the harvested crop takes off, and so what it takes to put it back.</p>
          </div>
        </div>

        {error && <p className="text-sm text-error-700">{error}</p>}
        {!data && !error && <div className="flex justify-center py-3"><Loader2 className="h-5 w-5 text-neutral-400 animate-spin" /></div>}

        {data && !data.crop_type && (
          <p className="text-sm text-neutral-500">This planting's cultivar has no crop type, so there is nothing to work from.</p>
        )}

        {data && data.crop_type && !hasRates && (
          <div className="space-y-2">
            <p className="text-sm text-neutral-600">
              No removal figures for {data.crop_type.name}{data.crop_type.parent_name ? ` or ${data.crop_type.parent_name}` : ''} yet.
            </p>
            <Button size="sm" variant="outline" onClick={editRates}>Add removal figures</Button>
          </div>
        )}

        {data && hasRates && (
          <>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Yield">
              {data.yields.map((y, i) => (
                <button
                  key={`${y.source}-${y.season_year}`}
                  type="button"
                  role="radio"
                  aria-checked={choice === i}
                  onClick={() => setChoice(i)}
                  className={cn('text-xs px-2.5 py-1.5 rounded-full border text-left',
                    choice === i ? 'bg-primary-50 border-primary-300 text-primary-800' : 'border-neutral-200 text-neutral-600')}
                >
                  {yieldLabel(y)}: {fmtNum(y.t_per_ha)} t/ha
                </button>
              ))}
              <label className={cn('text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5',
                choice === 'typed' ? 'bg-primary-50 border-primary-300 text-primary-800' : 'border-neutral-200 text-neutral-600')}>
                Target
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Target yield in tonnes a hectare"
                  className="w-14 bg-transparent border-b border-neutral-300 focus:outline-none text-right"
                  value={typed}
                  onFocus={() => setChoice('typed')}
                  onChange={(e) => { setTyped(e.target.value.replace(/[^0-9.]/g, '')); setChoice('typed'); }}
                />
                t/ha
              </label>
            </div>

            {lines.length > 0 ? (
              <ul className="divide-y divide-neutral-100">
                {lines.map((l) => (
                  <li key={l.nutrient} className="py-1.5 flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
                    <span className="text-neutral-800">{l.label}</span>
                    <span className="tabular-nums">
                      <span className="font-semibold text-neutral-900">{fmtNum(l.per_ha)}</span>
                      <span className="text-xs text-neutral-500"> {l.unit}</span>
                      {l.total != null && <span className="text-xs text-neutral-500"> · {fmtNum(l.total)} {l.total_unit} over {fmtNum(data.area.area_hectares)} ha</span>}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-neutral-500">Type a target yield to see what it takes off.</p>
            )}

            <p className="text-xs text-neutral-500">
              From the removal figures for {data.rates_from?.name}{data.rates_from?.inherited ? ` (none are set for ${data.crop_type?.name})` : ''}.
              {' '}Elemental P and K. What the crop takes off only: not what the trees store, what is lost, or what the soil already holds.
              {' '}A starting point to read with the soil results and your agronomist, not a recommendation.
              {' '}<button type="button" className="text-primary-600 hover:underline" onClick={editRates}>Change the figures</button>
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
